import { memo, useEffect } from "react";
import { Box, Card, CardActionArea, CardContent, Chip, Grid2, Typography, CircularProgress, List, ListItemButton, ListItemText, Divider } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import AIServicesNavbar from "../components/AIServicesNavbar";
import { useDispatch } from "react-redux";
import { handleErrorWithNotification } from "@/utils/errorHandling";

import { FETCH_SUBSCRIPTION_INFO } from "@company/graphql/queries/fetchSubscriptionInfo";
import { ServiceCreditType } from "@/app/graphql/codegen";
import { RouteTo } from "@routing/RouteMap";
import { useQuickJobs } from "@services/hooks/useQuickJobs";
import { useVsmeJobs } from "@services/hooks/useVsmeJobs";

/**
 * Dashboard-Seite für alle KI-Services.
 * Zeigt verfügbare Credits an und bietet eine Navigation zu den einzelnen Services.
 */
const AIServicesPage = memo(() => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { data, loading, error } = useQuery(FETCH_SUBSCRIPTION_INFO, {
        errorPolicy: "all",
    });

    // Mapping von ServiceCreditType -> verbleibende Nutzungen
    const credits: Record<string, number> = {};
    (data?.subscriptionInfo?.serviceCredits || []).forEach((c: { type: string; remainingUses: number }) => {
        credits[c.type] = (credits[c.type] || 0) + c.remainingUses;
    });

    const remainingQuickCredits = credits[ServiceCreditType.QuickWesentlichkeit] || 0;
    const remainingVsmeCredits = credits[ServiceCreditType.VsmeReport] || 0;

    const { pendingJobs: qwPending, completedJobs: qwCompleted, loading: qwLoading, error: qwError } = useQuickJobs();
    const { pendingJobs: vsmePending, completedJobs: vsmeCompleted, loading: vsmeLoading, error: vsmeError } = useVsmeJobs();

    const jobsLoading = qwLoading || vsmeLoading;
    const jobsError = qwError || vsmeError;

    // Error notifications
    useEffect(() => {
        if (error) {
            handleErrorWithNotification(error as Error, dispatch, "Fehler", "Service-Credits konnten nicht geladen werden");
        }
    }, [error, dispatch]);

    useEffect(() => {
        if (jobsError) {
            handleErrorWithNotification(jobsError as Error, dispatch, "Fehler", "Analysen konnten nicht geladen werden");
        }
    }, [jobsError, dispatch]);

    return (
        <Box sx={{ p: 3 }}>
            <Grid2 container spacing={3}>
                {/* Sidebar */}
                <Grid2 size={{ xs: 12, md: 3 }}>
                    <AIServicesNavbar />
                </Grid2>

                {/* Main content */}
                <Grid2 size={{ xs: 12, md: 9 }}>
                    <Typography variant="h4" gutterBottom>
                        KI-Services
                    </Typography>

                    <Grid2 container spacing={3} sx={{ mt: 1 }}>
                        {/* Quick-Wesentlichkeit Service */}
                        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                            <Card
                                sx={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}
                                elevation={3}
                            >
                                <CardActionArea
                                    onClick={() => navigate(`${RouteTo.AI_SERVICES}/wesentlichkeit`)}
                                    sx={{ height: "100%" }}
                                >
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Quick-Wesentlichkeitsanalyse
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            KI-gestützte Analyse der wesentlichen ESG-Themen für Ihr Unternehmen.
                                        </Typography>

                                        <Chip
                                            label={loading ? "…" : `${remainingQuickCredits} Credit${remainingQuickCredits !== 1 ? "s" : ""}`}
                                            color={remainingQuickCredits > 0 ? "primary" : "default"}
                                            size="small"
                                        />
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid2>

                        {/* VSME Report Generation */}
                        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                            <Card sx={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }} elevation={3}>
                                <CardActionArea onClick={() => navigate(`${RouteTo.AI_SERVICES}/vsme`)} sx={{ height: "100%" }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            VSME Report Generation
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            Automatisch generierter Kurzbericht basierend auf Ihrer Wesentlichkeitsanalyse.
                                        </Typography>

                                        <Chip
                                            label={loading ? "…" : `${remainingVsmeCredits} Credit${remainingVsmeCredits !== 1 ? "s" : ""}`}
                                            color={remainingVsmeCredits > 0 ? "primary" : "default"}
                                            size="small"
                                        />
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid2>
                    </Grid2>

                    {/* Job list */}
                    <Box sx={{ mt: 4 }}>
                        <Typography variant="h6" gutterBottom>
                            Letzte Analysen
                        </Typography>

                        {jobsLoading ? (
                            <CircularProgress size={24} />
                        ) : (
                            <List>
                                {[...qwPending, ...vsmePending].map((job) => (
                                    <ListItemButton
                                        key={job.id}
                                        onClick={() =>
                                            navigate(
                                                job.type === ServiceCreditType.VsmeReport
                                                    ? `${RouteTo.AI_SERVICES}/vsme/status/${job.id}`
                                                    : `${RouteTo.AI_SERVICES}/wesentlichkeit/status/${job.id}`,
                                            )
                                        }
                                    >
                                        <ListItemText
                                            primary={`Analyse in Bearbeitung (eingereicht ${new Date(job.submittedAt).toLocaleString()})`}
                                            secondary={`Status: ${job.status}`}
                                        />
                                    </ListItemButton>
                                ))}

                                {[...qwCompleted, ...vsmeCompleted].map((job) => (
                                    <ListItemButton
                                        key={job.id}
                                        onClick={() =>
                                            navigate(
                                                job.type === ServiceCreditType.VsmeReport
                                                    ? `${RouteTo.AI_SERVICES}/vsme/result/${job.id}`
                                                    : `${RouteTo.AI_SERVICES}/wesentlichkeit/result/${job.id}`,
                                            )
                                        }
                                    >
                                        <ListItemText
                                            primary={`Analyse vom ${new Date(job.submittedAt).toLocaleDateString()}`}
                                            secondary="Status: COMPLETED"
                                        />
                                    </ListItemButton>
                                ))}

                                {qwPending.length + vsmePending.length === 0 && qwCompleted.length + vsmeCompleted.length === 0 && (
                                    <Typography variant="body2">Noch keine Analysen durchgeführt.</Typography>
                                )}
                            </List>
                        )}
                    </Box>
                </Grid2>
            </Grid2>
        </Box>
    );
});

export default AIServicesPage; 